/**
 * The player's visual-effects level (story `q-demo-vfx-pass`): Full, Reduced (half the
 * particles, no screen flash) or Off. Persisted under `pxl.vfx`; the Settings panel writes
 * it through `setVfxLevel`, the director reads `vfxLevel()` on every burst, so a change
 * applies at once. With no stored choice, the Accessibility "reduce motion" preference
 * means Reduced. Pure apart from the optional storage handle.
 */

export const VFX_LEVELS = ['full', 'reduced', 'off'] as const;
export type VfxLevel = (typeof VFX_LEVELS)[number];
export const VFX_STORAGE_KEY = 'pxl.vfx';

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function storage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function parseVfxLevel(raw: string | null, reduceMotion: boolean): VfxLevel {
  if (raw === 'full' || raw === 'reduced' || raw === 'off') return raw;
  return reduceMotion ? 'reduced' : 'full';
}

let cached: VfxLevel | null = null;

export function vfxLevel(store: StorageLike | null = storage()): VfxLevel {
  if (cached !== null) return cached;
  cached = parseVfxLevel(
    store?.getItem(VFX_STORAGE_KEY) ?? null,
    store?.getItem('pxl.reduceMotion') === '1',
  );
  return cached;
}

export function setVfxLevel(level: VfxLevel, store: StorageLike | null = storage()): void {
  cached = level;
  try {
    store?.setItem(VFX_STORAGE_KEY, level);
  } catch {
    /* private mode: the choice holds for this page */
  }
}

/** Particle-count multiplier for a level (Off spawns nothing). */
export function countScaleFor(level: VfxLevel): number {
  return level === 'full' ? 1 : level === 'reduced' ? 0.5 : 0;
}

/** Test seam: forget the cached level. */
export function resetVfxLevelCache(): void {
  cached = null;
}
